// crewbit-admin/src/components/money/MoneyInvoiceDetailPanel.tsx
//
// Spec: §InvoiceDetail.7. Admin slide-over composing the invoice detail
// surface — status hero, dispute banner, action row, document card, payment
// history, activity timeline. Mirrors mobile §InvoiceDetail (Session 124)
// composition with admin web-runtime plumbing.
//
// Mounted from /money/outstanding via URL search-param (?invoice_id=...).
// `invoiceId === null` keeps the SlidePanel closed; transition animates.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidePanel } from '../common/SlidePanel';
import { Toast } from '../common/Toast';
import { palette, spacing, typography } from '../../styles/tokens';
import { useInvoiceDetail } from '../../hooks/useInvoiceDetail';
import type { Invoice } from '../../types';
import { usePaymentLedger } from '../../hooks/usePaymentLedger';
import { useInvoiceActivity } from '../../hooks/useInvoiceActivity';
import {
  invoiceActions,
  type ActionId,
  type InvoiceStatus,
} from '../../lib/invoiceActions';
import { sendReminder, sendReceipt } from '../../lib/invoiceMutations';
import { share, copyLink } from '../../lib/invoiceShare';
import { hostedPageUrl } from '../../lib/hostedPageUrl';
import { useAuthStore } from '../../stores/authStore';
import { StatusHero } from './StatusHero';
import { DisputeBanner } from './DisputeBanner';
import { InvoiceActionRow } from './InvoiceActionRow';
import { DocumentCard } from './DocumentCard';
import { InvoiceLinkedPhotoGallery } from './InvoiceLinkedPhotoGallery';
import { PaymentHistoryCard } from './PaymentHistoryCard';
import { ActivityTimelineCard } from './ActivityTimelineCard';
import { CancelInvoiceModal } from './CancelInvoiceModal';
import { DeleteDraftInvoiceModal } from './DeleteDraftInvoiceModal';
import { DuplicateInvoiceModal } from './DuplicateInvoiceModal';
import { MarkBadDebtModal } from './MarkBadDebtModal';
import { MarkPaidModal } from './MarkPaidModal';
import { RefundModal } from './RefundModal';
import { SaveAsShapeModal } from './SaveAsShapeModal';
import { SendInvoiceModal } from './SendInvoiceModal';

const REMINDER_RECENT_MS = 24 * 60 * 60 * 1000;

interface Props {
  invoiceId: string | null;
  onClose: () => void;
  /**
   * Optional Edit-action callback. Action matrix only emits 'edit' for
   * status === 'draft' (lib/invoiceActions.ts), so the parent can routing
   * the draft to the authoring panel without status-branching here. If
   * undefined, the action no-ops cleanly. Session 127 §3d.
   */
  onEditAction?: (invoice: Invoice) => void;
}

type ToastState = { message: string; type: 'success' | 'error' | 'info' } | null;

export function MoneyInvoiceDetailPanel({ invoiceId, onClose, onEditAction }: Props) {
  const isOpen = invoiceId !== null;
  const navigate = useNavigate();
  const businessSlug = useAuthStore((s) => s.business?.slug ?? '');
  const detail = useInvoiceDetail(invoiceId);
  const ledger = usePaymentLedger({ invoiceId });
  const activity = useInvoiceActivity(invoiceId);

  const [toast, setToast] = useState<ToastState>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteDraftOpen, setDeleteDraftOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [markBadDebtOpen, setMarkBadDebtOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [saveAsShapeOpen, setSaveAsShapeOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const inv = detail.data;

  // Effect-driven recency check — Date.now() is impure (forbidden in render
  // and useMemo bodies by react-hooks/purity), so it lives inside useEffect
  // and feeds state. Re-evaluates when last_reminder_sent_at changes.
  const [lastRemindedRecent, setLastRemindedRecent] = useState(false);
  const lastReminderIso = inv?.last_reminder_sent_at ?? null;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- derived from impure Date.now() (rule §3a/§InvoiceDetail.7.3)
    if (!lastReminderIso) { setLastRemindedRecent(false); return; }
    const ms = new Date(lastReminderIso).getTime();
    setLastRemindedRecent((Date.now() - ms) < REMINDER_RECENT_MS);
  }, [lastReminderIso]);

  const actions = useMemo(() => {
    if (!inv) return [];
    const list = invoiceActions({
      invoice: {
        status: inv.status as InvoiceStatus,
        is_disputed: inv.is_disputed ?? false,
        dispute_status: inv.dispute_status ?? null,
      },
      hasStripePaymentIntent: !!inv.stripe_payment_intent_id,
      lastRemindedRecent,
    });
    // Session 127 §3d — Edit gate lifted. Matrix only emits 'edit' for drafts;
    // parent routes via onEditAction callback (no status-branch here).
    return list;
  }, [inv, lastRemindedRecent]);

  const showToast = (msg: string, t: 'success' | 'error' | 'info') => setToast({ message: msg, type: t });

  const onResult = (ok: boolean, message: string) => showToast(message, ok ? 'success' : 'error');

  const handleAction = async (id: ActionId) => {
    if (!inv) return;
    switch (id) {
      case 'send':
        setSendOpen(true);
        break;
      case 'send_reminder': {
        const r = await sendReminder({ invoiceId: inv.id });
        showToast(
          r.success
            ? (r.data.idempotent_replay ? 'Reminder already queued.' : 'Reminder sent.')
            : (r.error.message ?? 'Failed to send reminder.'),
          r.success ? 'success' : 'error',
        );
        break;
      }
      case 'send_receipt': {
        const r = await sendReceipt({ invoiceId: inv.id });
        showToast(
          r.success
            ? (r.data.idempotent_replay ? 'Receipt already queued.' : 'Receipt sent.')
            : (r.error.message ?? 'Failed to send receipt.'),
          r.success ? 'success' : 'error',
        );
        break;
      }
      case 'record_payment':
        setMarkPaidOpen(true);
        break;
      case 'refund':
      case 'refund_remaining':
        // Stub-toast removed Phase 5 / §InvoiceRefund.StripePathUI — RefundModal
        // now branches by inv.stripe_payment_intent_id and routes to the
        // refund-invoice-via-stripe edge fn (Stripe path) or
        // mark_invoice_refunded_manually RPC (manual path).
        setRefundOpen(true);
        break;
      case 'cancel':
        setCancelOpen(true);
        break;
      case 'copy_link': {
        const r = await copyLink({ invoiceId: inv.id, slug: businessSlug });
        showToast(r.success ? 'Link copied.' : (r.error ?? 'Failed to copy link.'), r.success ? 'success' : 'error');
        break;
      }
      case 'share': {
        const r = await share({ invoiceId: inv.id, slug: businessSlug });
        if (r.success) showToast('Share opened.', 'success');
        else if (r.error !== 'aborted') showToast(r.error ?? 'Failed to share.', 'error');
        break;
      }
      case 'view_dispute':
        void navigate(`/money/disputes?invoice_id=${inv.id}`);
        break;
      case 'view_receipt':
        if (inv.share_token && businessSlug) {
          window.open(hostedPageUrl(businessSlug, 'pay', inv.share_token), '_blank');
        } else {
          showToast('Receipt unavailable — invoice has no share token.', 'error');
        }
        break;
      case 'mark_bad_debt':
        setMarkBadDebtOpen(true);
        break;
      case 'duplicate_as_draft':
        setDuplicateOpen(true);
        break;
      case 'delete':
        setDeleteDraftOpen(true);
        break;
      case 'edit':
        // Session 127 §3d — emit intent; parent routes draft → authoring panel.
        onEditAction?.(inv);
        break;
      case 'save_as_shape':
        setSaveAsShapeOpen(true);
        break;
    }
  };

  const subtitle = inv ? statusSubtitle(inv.status) : '';

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={onClose}
        title={inv?.invoice_number ?? 'Invoice'}
        subtitle={subtitle}
        width={720}
      >
        {detail.isLoading && !inv && <SkeletonBody />}

        {!detail.isLoading && detail.error && !inv && (
          <ErrorBody onRetry={() => void detail.refetch()} />
        )}

        {!detail.isLoading && !detail.error && !inv && invoiceId && (
          <NotFoundBody onClose={onClose} />
        )}

        {inv && (
          <>
            <DisputeBanner invoice={inv} onTap={() => navigate(`/money/disputes?invoice_id=${inv.id}`)} />
            <StatusHero invoice={inv} />
            <InvoiceActionRow actions={actions} onAction={(id) => void handleAction(id)} />
            <DocumentCard invoice={inv} />
            <InvoiceLinkedPhotoGallery invoiceId={inv.id} />
            <PaymentHistoryCard ledgerRows={ledger.data ?? []} invoice={inv} />
            <ActivityTimelineCard activity={activity.data} loading={activity.isLoading} />
          </>
        )}
      </SlidePanel>

      {inv && (
        <>
          <CancelInvoiceModal
            isOpen={cancelOpen}
            invoiceId={inv.id}
            onClose={() => setCancelOpen(false)}
            onResult={onResult}
          />
          <DeleteDraftInvoiceModal
            isOpen={deleteDraftOpen}
            invoiceId={inv.id}
            invoiceNumber={inv.invoice_number ?? null}
            onClose={() => {
              setDeleteDraftOpen(false);
              onClose();
            }}
            onResult={onResult}
          />
          <DuplicateInvoiceModal
            isOpen={duplicateOpen}
            invoiceId={inv.id}
            onClose={() => setDuplicateOpen(false)}
            onResult={onResult}
            onSuccess={(newId) => {
              onClose();
              void navigate(`/money/invoices?invoice_id=${newId}`);
            }}
          />
          <MarkBadDebtModal
            isOpen={markBadDebtOpen}
            invoiceId={inv.id}
            onClose={() => setMarkBadDebtOpen(false)}
            onResult={onResult}
          />
          <MarkPaidModal
            isOpen={markPaidOpen}
            invoiceId={inv.id}
            defaultAmount={inv.total}
            onClose={() => setMarkPaidOpen(false)}
            onResult={onResult}
          />
          <RefundModal
            isOpen={refundOpen}
            invoiceId={inv.id}
            total={inv.total}
            alreadyRefunded={inv.refund_amount ?? 0}
            stripePaymentIntentId={inv.stripe_payment_intent_id}
            onClose={() => setRefundOpen(false)}
            onResult={onResult}
          />
          <SendInvoiceModal
            isOpen={sendOpen}
            invoiceId={inv.id}
            clientEmail={inv.client_email}
            onClose={() => setSendOpen(false)}
            onResult={onResult}
          />
          <SaveAsShapeModal
            isOpen={saveAsShapeOpen}
            invoiceId={inv.id}
            onClose={() => setSaveAsShapeOpen(false)}
            onResult={onResult}
          />
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

function statusSubtitle(status: string): string {
  switch (status) {
    case 'draft': return 'Draft';
    case 'sent': return 'Sent';
    case 'viewed': return 'Viewed';
    case 'overdue': return 'Overdue';
    case 'paid': return 'Paid';
    case 'partially_refunded': return 'Partially refunded';
    case 'refunded': return 'Refunded';
    case 'cancelled': return 'Cancelled';
    case 'written_off': return 'Written off';
    default: return status;
  }
}

function SkeletonBody() {
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: i === 1 ? 80 : 140,
            background: palette.g100,
            borderRadius: 12,
            marginBottom: spacing.md,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  );
}

function ErrorBody({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      style={{
        background: palette.amberWash,
        border: `1px solid ${palette.amber}`,
        borderRadius: 12,
        padding: spacing.md,
        fontSize: typography.size.caption,
        color: palette.g800,
      }}
    >
      <div style={{ fontWeight: typography.weight.semibold, marginBottom: spacing.xs }}>
        Couldn't load invoice
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: spacing.sm,
          padding: '6px 14px', borderRadius: 999, border: `1px solid ${palette.amber}`,
          background: palette.white, color: palette.amber,
          fontSize: typography.size.caption, fontWeight: typography.weight.semibold,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Retry
      </button>
    </div>
  );
}

function NotFoundBody({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        textAlign: 'center', padding: spacing.xl,
        fontSize: typography.size.caption, color: palette.g600,
      }}
    >
      <div style={{ fontWeight: typography.weight.semibold, color: palette.g800, marginBottom: spacing.xs }}>
        Invoice not found
      </div>
      <div style={{ marginBottom: spacing.md }}>
        It may have been deleted or you may not have access.
      </div>
      <button
        onClick={onClose}
        style={{
          padding: '8px 18px', borderRadius: 999, border: `1px solid ${palette.g200}`,
          background: palette.white, color: palette.g700,
          fontSize: typography.size.caption, fontWeight: typography.weight.medium,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Close
      </button>
    </div>
  );
}
